// The one Tamagui configuration: tokens (space, size, radius, type), the four palettes as themes,
// the two fonts and the media queries every component may branch on. Components never hold a
// literal colour, pixel or font name; they reference these.
import { createFont, createTamagui, createTokens } from "tamagui";
import { themes } from "./theme";

const space = {
  0: 0,
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  7: 32,
  8: 40,
  9: 56,
  10: 72,
} as const;

/** Control and icon sizes; 48px is the touch-target floor (docs/design-system.md). */
const size = { ...space, 0: 0, 4: 48, 5: 52, 6: 56, 7: 64, 8: 80, 9: 120, 10: 240 } as const;

const radius = { 0: 0, 1: 8, 2: 12, 3: 16, 4: 22, pill: 999 } as const;

const zIndex = { 0: 0, 1: 10, 2: 20, 3: 30, 4: 40, 5: 50 } as const;

const tokens = createTokens({ space, size, radius, zIndex, color: {} });

/** Newsreader for display, Manrope for everything read; one scale for both. */
// Type sizes scale with the accessible version, so they are CSS calc()s; createFont types them as numbers.
// oxlint-disable-next-line typescript/no-unsafe-type-assertion -- the only place a string stands in for a size
const px = (n: number): number => `calc(${n}px * var(--uiScale))` as unknown as number;
const scale = {
  size: {
    0: px(9),
    1: px(11),
    2: px(12),
    3: px(13),
    4: px(15),
    5: px(17),
    6: px(19),
    7: px(22),
    8: px(30),
  },
  lineHeight: {
    0: px(10),
    1: px(14),
    2: px(16),
    3: px(18),
    4: px(21),
    5: px(24),
    6: px(26),
    7: px(28),
    8: px(36),
  },
  weight: { 4: "400", 5: "500", 6: "600", 7: "700" },
  letterSpacing: { 4: 0, 8: -0.3 },
} as const;

const body = createFont({ family: '"Manrope", system-ui, sans-serif', ...scale });
const heading = createFont({ family: '"Newsreader", Georgia, serif', ...scale });

export const config = createTamagui({
  tokens,
  fonts: { body, heading },
  themes,
  media: {
    touch: { pointer: "coarse" },
    hoverable: { hover: "hover" },
    narrow: { maxWidth: 380 },
    wide: { minWidth: 768 },
    /** The tab bar becomes a left rail from here. */
    rail: { minWidth: 820 },
    /** A laptop gets a wider reading column. */
    desk: { minWidth: 1100 },
    landscape: { orientation: "landscape" },
  },
  // "legacy": flex={1} means grow, shrink, auto basis. The web default (basis 0) collapses an item
  // inside a column whose height comes from its content, which is most of this app.
  settings: {
    styleCompat: "legacy",
    defaultFont: "body",
    allowedStyleValues: "somewhat-strict-web",
  },
});

export type ProefConfig = typeof config;

declare module "tamagui" {
  interface TamaguiCustomConfig extends ProefConfig {}
}
